import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  Coupon,
  CouponPage,
  CouponPreview,
  CouponRedemptionPage,
  CreateCouponInput,
  OrderWithItems,
  PreviewCouponInput,
  UpdateCouponInput,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import {
  applyCouponToOrder,
  createCoupon,
  getCouponRedemptions,
  getCoupons,
  previewCoupon,
  removeCouponFromOrder,
  updateCoupon,
  type CouponListParams,
} from './coupons-api.js';

/**
 * The coupon hooks.
 *
 * The preview is a MUTATION rather than a query, and that is deliberate rather than a
 * misreading of the verb: it is fired when somebody presses Apply, not on every keystroke, and
 * caching "what would DEEPAVALI20 do to a ₹1,000 bill" would answer the next customer with the
 * previous customer's usage counts.
 */

export const couponKeys = {
  all: ['coupons'] as const,
  list: (params: CouponListParams) => ['coupons', 'list', params] as const,
  redemptions: (couponId: string) => ['coupons', 'redemptions', couponId] as const,
};

export function useCoupons(
  params: CouponListParams = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<CouponPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: couponKeys.list(params),
    queryFn: () => getCoupons(client, params),
    enabled: options.enabled ?? true,
  });
}

export function useCouponRedemptions(
  couponId: string | null,
): UseQueryResult<CouponRedemptionPage> {
  const client = useApiClient();
  return useQuery({
    queryKey: couponKeys.redemptions(couponId ?? ''),
    queryFn: () => getCouponRedemptions(client, couponId ?? ''),
    enabled: couponId !== null,
  });
}

export function useCreateCoupon(): UseMutationResult<Coupon, unknown, CreateCouponInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCouponInput) => createCoupon(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: couponKeys.all });
    },
  });
}

export function useUpdateCoupon(): UseMutationResult<
  Coupon,
  unknown,
  { couponId: string } & UpdateCouponInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ couponId, ...input }: { couponId: string } & UpdateCouponInput) =>
      updateCoupon(client, couponId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: couponKeys.all });
    },
  });
}

export function usePreviewCoupon(): UseMutationResult<CouponPreview, unknown, PreviewCouponInput> {
  const client = useApiClient();
  return useMutation({
    mutationFn: (input: PreviewCouponInput) => previewCoupon(client, input),
  });
}

/**
 * Putting a code on a bill that already exists.
 *
 * Invalidates the ORDERS as well as the coupons: applying one changes the bill's total, and a
 * screen that refreshed only the coupon list would leave the order still showing what it cost
 * before the discount.
 */
export function useApplyOrderCoupon(): UseMutationResult<
  OrderWithItems,
  unknown,
  { orderId: string; code: string }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, code }: { orderId: string; code: string }) =>
      applyCouponToOrder(client, orderId, code),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: couponKeys.all });
    },
  });
}

export function useRemoveOrderCoupon(): UseMutationResult<OrderWithItems, unknown, string> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => removeCouponFromOrder(client, orderId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void queryClient.invalidateQueries({ queryKey: couponKeys.all });
    },
  });
}
